import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  Card,
  CardContent,
  useTheme,
  alpha
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AdminPanelSettings as ManagementIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import FilterBar from '../../common/FilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { MANAGEMENT_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { motion } from 'framer-motion';
import { glassCardSx, SUCCESS, TEXT_PRIMARY, TEXT_SECONDARY } from '../../common/SaaSTokens';

const ManagementList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  
  
  const MotionCard = motion(Card);
  

  const [managements, setManagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedManagement, setSelectedManagement] = useState(null);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  const filterOptions = [
    {
      name: 'description',
      label: 'Description Status',
      type: 'select',
      options: [
        { value: 'has', label: 'Has Description' },
        { value: 'none', label: 'No Description' }
      ]
    }
  ];

  useEffect(() => {
    fetchManagements({ forceRefresh: Boolean(location.state?.refreshList) });
  }, [location.pathname, location.state?.refreshList]);

  const fetchManagements = async ({ forceRefresh = false } = {}) => {
    try {
      setLoading(true);
      const response = await api.get('/managements', forceRefresh ? { cache: false } : {});
      setManagements(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching managements:', error);
      setError('Failed to load managements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (management) => {
    setSelectedManagement(management);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setConfirmDeleteLoading(true);
      setSuccess(null);
      setError(null);
      await api.delete(`/managements/${selectedManagement.id}`);
      setManagements(managements.filter(m => m.id !== selectedManagement.id));
      setSuccess('Management deleted successfully');
    } catch (error) {
      console.error('Error deleting management:', error);
      setError('Failed to delete management. Please try again.');
    } finally {
      setConfirmDeleteLoading(false);
      setDeleteDialogOpen(false);
      setSelectedManagement(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    const nextActive = newFilters.description === 'has' ? 'has' : newFilters.description === 'none' ? 'none' : 'all';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'has', 'none'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.description;
    } else {
      nextFilters.description = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'has' ? 1 :
    activeStatFilter === 'none' ? 2 :
    0;

  const filteredManagements = managements.filter(management => {
    const matchesSearch = management.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (management.description && management.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (key === 'description') {
        if (value === 'has') return management.description;
        if (value === 'none') return !management.description;
      }
      return true;
    });

    return matchesSearch && matchesFilters;
  });

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    { icon: ManagementIcon, value: managements.length, label: 'Total Managements', color: 'info' },
    { icon: DescriptionIcon, value: managements.filter(m => m.description).length, label: 'With Description', color: 'success' },
    { icon: DescriptionIcon, value: managements.filter(m => !m.description).length, label: 'No Description', color: 'warning' },
  ];

  // Define columns for TableList
  const columns = [
    {
      key: 'name',
      label: 'Name',
      icon: ManagementIcon,
      minWidth: 200,
      render: (management) => (
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {management.name}
        </Typography>
      )
    },
    {
      key: 'description',
      label: 'Description',
      minWidth: 300,
      render: (management) => (
        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9) }}> 
          {management.description || (
            <span style={{ color: theme.palette.text.disabled }}>-</span>
          )}
        </Typography>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 140,
      render: (management) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Management" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/managements/${management.id}`); }}
              size="small"
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Management" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/managements/edit/${management.id}`); }}
              size="small"
              sx={{
                color: theme.palette.warning.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Management" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); handleDelete(management); }}
              size="small"
              sx={{
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Loading message="Loading managements list" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Header
        title="Management"
        subtitle="Manage organization management levels"
        icon={ManagementIcon}
        actionButton={
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/managements/add')}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
              borderRadius: 2,
              px: 3,
              py: 1.5,
              alignSelf: { xs: 'stretch', sm: 'auto' }
            }}
          >
            Add Management
          </Button>
        }
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          boxShadow: '0 4px 20px rgba(34,197,94,0.12)',
          color: alpha(SUCCESS, 0.95),
          mb: 3,
        }}
      />

      {/* Stat Cards */}
      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      {/* Enhanced Filter Bar */}
      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.16 }}
        elevation={0}
        sx={{ ...glassCardSx, mb: 2.5 }}
      >
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
            searchPlaceholder="Search by management name or description..."
          />
        </CardContent>
      </MotionCard>

      {/* Managements Table */}
      <ListBulkToolbar
        config={MANAGEMENT_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: MANAGEMENT_BULK_CONFIG.endpoint,
          setItems: setManagements,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'management role(s)',
          refetch: fetchManagements,
          setError
        })}
      />


      <TableList
        columns={columns}
        data={filteredManagements}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...bulkTableProps(MANAGEMENT_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)}
        loading={loading}
        onRowClick={(management) => navigate(`/managements/${management.id}`)}
        emptyState={{
          icon: <ManagementIcon />,
          title: 'No managements found',
          description: searchTerm || Object.keys(filters).length > 0
            ? "Try adjusting your filters to see more managements."
            : "Get started by adding your first management."
        }}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedManagement(null);
        }}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        itemType="management"
        itemName={selectedManagement?.name || ''}
        description="This action cannot be undone and will permanently remove all management data."
        confirmLabel="Delete Management"
        loading={confirmDeleteLoading}
      />

    </Container>
  );
};

export default ManagementList;